// frontend/src/components/ai/AIChatBubble.js
import React, { useRef, useEffect, useCallback } from 'react';
import { FaCommentDots } from 'react-icons/fa';
import { useCompare } from '../../context/CompareContext';

// ─── Constants ────────────────────────────────────────────────────────────────
const BUBBLE_SIZE    = 56;
const RIGHT_OFFSET   = 24;
const BOTTOM_DESKTOP = 24;
const BOTTOM_MOBILE  = 96;
const BOTTOM_COMPARE = 224;
const DRAG_THRESHOLD = 4;
const SNAP_DURATION  = 300;
const EDGE_MARGIN    = 8;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function getDefaultPos(compareActive) {
  const vw     = window.innerWidth;
  const vh     = window.innerHeight;
  const bottom = vw >= 768 ? BOTTOM_DESKTOP : compareActive ? BOTTOM_COMPARE : BOTTOM_MOBILE;
  return {
    x: vw - BUBBLE_SIZE - RIGHT_OFFSET,
    y: vh - BUBBLE_SIZE - bottom,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
function AIChatBubble({ onOpenChat }) {
  const { compareList }  = useCompare();
  const compareActive    = compareList.length > 0;

  const btnRef           = useRef(null);
  const posRef           = useRef(getDefaultPos(compareActive));
  const dragRef          = useRef(null);   // null = not dragging
  const snappingRef      = useRef(false);
  const wasDragRef       = useRef(false);
  const onOpenChatRef    = useRef(onOpenChat);
  const snapRafRef       = useRef(null);

  // Always keep onOpenChat fresh
  useEffect(() => { onOpenChatRef.current = onOpenChat; }, [onOpenChat]);

  // ── Write position straight to DOM — zero React re-renders ──────────────────
  const applyPos = useCallback((x, y) => {
    if (!btnRef.current) return;
    btnRef.current.style.left = `${x}px`;
    btnRef.current.style.top  = `${y}px`;
    posRef.current = { x, y };
  }, []);

  // ── Mount: set initial position ─────────────────────────────────────────────
  useEffect(() => {
    const p = getDefaultPos(compareActive);
    applyPos(p.x, p.y);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize: re-anchor if not dragged ────────────────────────────────────────
  useEffect(() => {
    const onResize = () => {
      if (dragRef.current || snappingRef.current) return;
      applyPos(...Object.values(getDefaultPos(compareActive)));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [compareActive, applyPos]);

  // ── Spring-snap to nearest edge ─────────────────────────────────────────────
  const snapToEdge = useCallback((fromX, fromY) => {
    if (snapRafRef.current) cancelAnimationFrame(snapRafRef.current);

    const vw   = window.innerWidth;
    const vh   = window.innerHeight;
    const toX  = (fromX + BUBBLE_SIZE / 2) < vw / 2
      ? EDGE_MARGIN
      : vw - BUBBLE_SIZE - EDGE_MARGIN;
    const toY  = clamp(fromY, EDGE_MARGIN, vh - BUBBLE_SIZE - EDGE_MARGIN);
    const sX   = fromX, sY = fromY;
    const t0   = performance.now();
    snappingRef.current = true;

    const frame = (now) => {
      const t = Math.min((now - t0) / SNAP_DURATION, 1);
      applyPos(sX + (toX - sX) * easeOutCubic(t), sY + (toY - sY) * easeOutCubic(t));
      if (t < 1) {
        snapRafRef.current = requestAnimationFrame(frame);
      } else {
        snappingRef.current = false;
        snapRafRef.current  = null;
      }
    };
    snapRafRef.current = requestAnimationFrame(frame);
  }, [applyPos]);

  // ── Global pointermove — attached to window so fast drags never "escape" ────
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;

      const dx = e.clientX - d.startPX;
      const dy = e.clientY - d.startPY;

      if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      d.moved          = true;
      wasDragRef.current = true;

      // Cancel any in-progress snap
      if (snapRafRef.current) {
        cancelAnimationFrame(snapRafRef.current);
        snapRafRef.current  = null;
        snappingRef.current = false;
      }

      if (d.rafId !== null) return; // already have a frame queued

      d.rafId = requestAnimationFrame(() => {
        d.rafId = null;
        if (!dragRef.current) return;
        applyPos(
          clamp(d.startEX + (e.clientX - d.startPX), 0, window.innerWidth  - BUBBLE_SIZE),
          clamp(d.startEY + (e.clientY - d.startPY), 0, window.innerHeight - BUBBLE_SIZE),
        );
      });
    };

    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      if (d.rafId !== null) cancelAnimationFrame(d.rafId);

      // Reset button visuals
      if (btnRef.current) {
        btnRef.current.style.transition = 'transform 0.2s, box-shadow 0.2s';
        btnRef.current.style.transform  = 'scale(1)';
        btnRef.current.style.boxShadow  = '';
        btnRef.current.style.cursor     = 'grab';
      }

      if (d.moved) {
        snapToEdge(posRef.current.x, posRef.current.y);
      }

      dragRef.current = null;
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup',   onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [applyPos, snapToEdge]);

  // ── Pointer down on the button ───────────────────────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault(); // prevents text selection & touch scroll stealing

    snappingRef.current = false;
    wasDragRef.current  = false;

    dragRef.current = {
      startPX: e.clientX,
      startPY: e.clientY,
      startEX: posRef.current.x,
      startEY: posRef.current.y,
      moved:   false,
      rafId:   null,
    };

    if (btnRef.current) {
      btnRef.current.style.transition = 'transform 0.12s, box-shadow 0.12s';
      btnRef.current.style.transform  = 'scale(1.13)';
      btnRef.current.style.boxShadow  = '0 18px 36px rgba(0,0,0,0.28)';
      btnRef.current.style.cursor     = 'grabbing';
    }
  }, []);

  // ── Click: tap = open chat, drag = suppress ──────────────────────────────────
  const onClick = useCallback((e) => {
    if (wasDragRef.current) {
      e.preventDefault();
      e.stopPropagation();
      wasDragRef.current = false;
      return;
    }
    if (typeof onOpenChatRef.current === 'function') {
      onOpenChatRef.current();
    }
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <button
      ref={btnRef}
      onPointerDown={onPointerDown}
      onClick={onClick}
      aria-label="Open AI Chat"
      style={{
        position:         'fixed',
        width:            BUBBLE_SIZE,
        height:           BUBBLE_SIZE,
        zIndex:           9999,
        cursor:           'grab',
        touchAction:      'none',
        userSelect:       'none',
        WebkitUserSelect: 'none',
        willChange:       'left, top, transform',
        transition:       'transform 0.2s, box-shadow 0.2s',
      }}
      className="
        rounded-full
        bg-primary hover:bg-primary-dark text-white
        flex items-center justify-center
        border-2 border-white
        shadow-lg
      "
    >
      <FaCommentDots size={28} />
    </button>
  );
}

export default AIChatBubble;